"use client";

import React, { useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { ProductDetailPage } from "@/components/product/ProductDetailPage";
import { useAuth } from "@/contexts/AuthContext";
import { LANDING_PAGE_PATH } from "@/lib/landing-page";
import { popCachedPDPProduct, productStore } from "@/lib/product-store";
import { getProductAnyId, type Product } from "@/lib/product-utils";
import { hydrateProductByUuid } from "@/lib/shop-agent-client";

import {
  navigateBackToShopping,
  ProductBreadcrumbs,
  getProductBreadcrumbs,
  useScrollToTopOnProductNavigation,
} from "./product-page-shared";

/**
 * Try in-memory product store first (populated when ProductCards render),
 * then fall back to localStorage (written on explicit PDP navigation).
 */
function hasFullProductPayload(product: Product): boolean {
  const record = product as unknown as Record<string, unknown>;
  return (
    record.enrichment !== undefined ||
    record.description !== undefined ||
    record.hasVariant !== undefined ||
    record.offers !== undefined
  );
}

function getCachedProduct(productId: string): Product | null {
  const fromStore = productStore.getProduct(productId);
  if (fromStore && getProductAnyId(fromStore) === productId && hasFullProductPayload(fromStore))
    return fromStore;

  const fromStorage = popCachedPDPProduct(productId);
  if (
    fromStorage &&
    getProductAnyId(fromStorage) === productId &&
    hasFullProductPayload(fromStorage)
  )
    return fromStorage;

  return null;
}

interface ProductPageClientProps {
  productId: string;
}

/**
 * Client-only fallback PDP flow used for smoke tests and non-canonical app-only
 * navigations that intentionally bypass the server-rendered route path.
 *
 * Uses a two-tier warm cache (in-memory productStore, then localStorage)
 * so navigating from search results / quickview skips the API round-trip.
 * Falls back to hydrateProductByUuid when no cached data is available.
 */
export default function ProductPageClient({ productId }: ProductPageClientProps) {
  const router = useRouter();
  const { loading: authLoading } = useAuth();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useScrollToTopOnProductNavigation(productId);

  // Check warm cache (in-memory productStore + localStorage) first, then
  // fall back to API fetch. Runs in useEffect to avoid SSR/client hydration
  // mismatch (localStorage is only available on the client).
  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    // Reset state so a stale product from a previous ID is never shown
    // while the new product loads.
    setProduct(null);
    setLoading(true);

    const cached = getCachedProduct(productId);
    if (cached) {
      setProduct(cached);
      setLoading(false);
      return;
    }

    if (authLoading) {
      return;
    }

    let cancelled = false;

    const fetchProduct = async () => {
      try {
        const fetchedProduct = await hydrateProductByUuid(productId);
        if (cancelled) return;
        if (fetchedProduct) {
          setProduct(fetchedProduct as unknown as Product);
        } else {
          setProduct(null);
        }
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading product:", error);
        setProduct(null);
        setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      cancelled = true;
    };
  }, [productId, authLoading]);

  const breadcrumbPath = React.useMemo(
    () =>
      product && getProductAnyId(product) === productId ? getProductBreadcrumbs(product) : null,
    [product, productId]
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-gray-400 border-t-transparent" />
          <p className="mt-3 text-base font-medium text-gray-700">Loading product...</p>
        </div>
      </div>
    );
  }

  // Show not found state if product couldn't be loaded
  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-md text-center">
          <h2 className="mb-2 text-2xl font-semibold text-gray-800">Product Not Found</h2>
          <p className="mb-6 text-gray-600">
            We couldn&apos;t find the product you&apos;re looking for. It may have been removed or
            the link might be incorrect.
          </p>
          <button
            onClick={() => router.push(LANDING_PAGE_PATH)}
            className="rounded-lg bg-black px-6 py-2 text-white transition-colors hover:bg-gray-800"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const handleBackToShopping = () => {
    navigateBackToShopping(router);
  };

  return (
    <div>
      <ProductBreadcrumbs
        key={productId}
        productId={productId}
        path={breadcrumbPath}
        router={router}
      />

      <ProductDetailPage product={product} onBackToShopping={handleBackToShopping} />
    </div>
  );
}
